// Native inputs own values, edits, undo, clipboard, caret and selection. Generic
// amounts use a contextual input font with the primary display's exact glyph
// advances; the overlay is decorative in every focus state. Card limits retain
// their existing native-advance presentation.
const instances = new WeakMap();
const escapePattern = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const kebab = value => value.replace(/[A-Z]/g, character => `-${character.toLowerCase()}`);

const nativeFonts = new Map();

function nativeFont(family, decimal) {
  if (nativeFonts.has(family)) return nativeFonts.get(family);
  const state = { ready: false };
  nativeFonts.set(family, state);
  state.promise = Promise.resolve().then(async () => {
    if (!document.fonts || !CSS.supports('font-feature-settings', '"calt" 1')) return;
    const [faces, integers, separators] = await Promise.all([
      document.fonts.load(`500 24px "${family}"`, `8${decimal}8888`),
      document.fonts.load('500 24px "Poppins"', '0123456789 ABCDEFGHIJKLMNOPQRSTUVWXYZ'),
      document.fonts.load('400 16px "Poppins"', '.,'),
    ]);
    // An undeclared family resolves to an empty set, not a failed promise.
    // The local input font may load before the display's Poppins faces; wait
    // for both, including the actual weight rather than a nearest-weight face.
    const loadedWeight = (loaded, weight) => loaded.length &&
      loaded.every(face => face.status === 'loaded') && loaded.some(face => {
        const values = face.weight === 'normal' ? [400] : face.weight.split(/\s+/).map(Number);
        return values.length === 1 ? values[0] === weight : values[0] <= weight && weight <= values[1];
      });
    if (!loadedWeight(faces, 500) || !loadedWeight(integers, 500) || !loadedWeight(separators, 400)) return;
    const probe = document.createElement('span');
    probe.setAttribute('aria-hidden', 'true');
    Object.assign(probe.style, { position: 'absolute', left: '-10000px', top: '0',
      width: 'max-content', whiteSpace: 'pre', font: `500 24px/36px "${family}"`,
      fontKerning: 'none', fontFeatureSettings: '"calt" 1', letterSpacing: '0', textRendering: 'geometricPrecision' });
    const reference = probe.cloneNode();
    reference.style.fontFamily = 'Poppins';
    reference.style.fontKerning = 'normal';
    document.body.append(probe, reference);
    try {
      const measure = value => { probe.textContent = value; return probe.getBoundingClientRect().width; };
      const primaryWidth = (value, size, weight) => {
        reference.textContent = value;
        reference.style.fontSize = `${size}px`;
        reference.style.fontWeight = weight;
        return reference.getBoundingClientRect().width;
      };
      // Compare actual display-font advances, not only a scale ratio: FreeType
      // autohinting can change a separator's advance at different ppem while
      // still preserving the ratio for a particular digit (such as eight).
      // This bounded check runs once per family and covers both separators,
      // narrow/wide digits and every supported fractional length.
      state.ready = ['.', ','].every(separator =>
        Math.abs(measure(separator) - primaryWidth(separator, 16, 400)) < 0.15) &&
        ['1', '8'].every(digit => {
          const whole = primaryWidth(digit, 24, 500);
          const separator = primaryWidth(decimal, 16, 400);
          return Math.abs(measure(digit) - whole) < 0.15 && [1, 2, 3, 4].every(count =>
            Math.abs(measure(`${digit}${decimal}${digit.repeat(count)}`) -
              (whole + separator + primaryWidth(digit.repeat(count), 12.8, 500))) < 0.15);
        });
    } finally {
      probe.remove();
      reference.remove();
    }
  }).catch(() => { state.ready = false; });
  return state;
}

export function createMoneyInputPresentation(display, readFormat, { namespace = 'money-input-presentation', dataPrefix = 'moneyInput' } = {}) {
  const field = display.closest(`.${namespace}__field`);
  if (!field) return { refresh() {}, destroy() {} };
  const inlineCurrency = namespace === 'money-input-presentation' &&
    field.classList.contains('money-input-presentation__field--inline-currency');
  const data = kebab(dataPrefix);
  let overlay = field.querySelector(`[data-${data}-overlay]`);
  const createdOverlay = !overlay;
  if (!overlay) {
    overlay = document.createElement('span');
    overlay.className = `${namespace}__overlay`;
    overlay.dataset[`${dataPrefix}Overlay`] = '';
    overlay.setAttribute('aria-hidden', 'true');
    const viewport = document.createElement('span');
    viewport.className = `${namespace}__viewport`;
    const text = document.createElement('span');
    text.className = `${namespace}__text`;
    text.dataset[`${dataPrefix}Presentation`] = '';
    viewport.append(text);
    overlay.append(viewport);
    field.append(overlay);
  }
  const text = overlay.querySelector(`[data-${data}-presentation]`);
  if (!text) return { refresh() {}, destroy() {} };
  overlay.hidden = true;
  field.classList.remove(`${namespace}__field--split`, `${namespace}__field--compact`, `${namespace}__field--editing`);
  let previousValue;
  let previousPrimaryKey;
  let frame;
  let composing = false;
  let destroyed = false;
  const viewport = text.parentElement;
  const generic = namespace === 'money-input-presentation';
  const requestedFonts = new Set();
  let appliedFont;
  const nativeOverflow = generic && inlineCurrency && display.hasAttribute('data-money-input-native-overflow');
  let lastNativeValue;
  let lastNativeWidth;

  function applyNativeFont(family) {
    if (!generic || appliedFont === family) return;
    appliedFont = family;
    field.classList.toggle(`${namespace}__field--native`, Boolean(family));
    if (family) display.style.setProperty('--money-input-native-font', `"${family}"`);
    else display.style.removeProperty('--money-input-native-font');
    // Autosized card fields can recompute their existing measurer without an
    // input/change event or a mutation of AutoNumeric's value/undo history.
    display.dispatchEvent(new Event('money-input:font-ready'));
  }

  function hide() {
    overlay.hidden = true;
    field.classList.remove(`${namespace}__field--split`, `${namespace}__field--compact`, `${namespace}__field--editing`);
    applyNativeFont(undefined);
    field.classList.remove(`${namespace}__field--native-overflow`, `${namespace}__field--currency-wrap`);
    delete field.dataset.moneyScrollStart;
    delete field.dataset.moneyScrollEnd;
    lastNativeValue = undefined;
    lastNativeWidth = undefined;
  }

  function selectionChange() {
    if (document.activeElement === display) refresh();
  }

  function renderPrimary() {
    if (composing || display.disabled || display.validity.customError) return hide();
    const { decimal, group, precision, suffix = false } = readFormat();
    if (!decimal || !group || !Number.isInteger(precision) || precision < 0 || precision > 4) return hide();
    const family = decimal === ',' && group === '.' ? 'Arcadi Money ES' :
      decimal === '.' && group === ',' ? 'Arcadi Money EN' : null;
    if (!family) return hide();
    const font = nativeFont(family, decimal);
    if (!requestedFonts.has(family)) {
      requestedFonts.add(family);
      font.promise.then(refresh);
    }
    if (!font.ready) return hide();
    const whole = `(?:\\d+|[1-9]\\d{0,2}(?:${escapePattern(group)}\\d{3})+)`;
    const fraction = precision > 0 ? `(?:${escapePattern(decimal)}(?<fraction>\\d{0,${precision}}))?` : '';
    const currency = suffix ? '(?: (?<currency>[A-Z]{2,8}))?' : '';
    const match = new RegExp(`^(?<whole>${whole})${fraction}${currency}$`).exec(display.value);
    if (!match) return hide();
    // Some amount forms autosize a numeric-only input beside its currency.
    // Read that existing label; metadata must never invent a displayed unit.
    const currencyLabel = inlineCurrency ? field.querySelector(':scope > [data-money-input-currency-label]') : null;
    const displayedCurrency = match.groups.currency || currencyLabel?.textContent.trim() || '';
    text.classList.add('money-amount', 'money-amount--primary');
    const styled = getComputedStyle(field).getPropertyValue('--money-input-overlay-ready').trim() === '1' &&
      getComputedStyle(text).getPropertyValue('--money-amount-primary-ready').trim() === '1';
    if (!styled) return hide();

    applyNativeFont(family);
    const style = getComputedStyle(display);
    field.style.setProperty('--money-input-ink', style.color);
    ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
      'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'borderRadius']
      .forEach(property => { overlay.style[property] = style[property]; });
    overlay.style.top = inlineCurrency ? '0px' : `${display.offsetTop}px`;
    overlay.style.left = inlineCurrency ? '0px' : `${display.offsetLeft}px`;
    overlay.style.width = inlineCurrency ? '100%' : `${display.offsetWidth}px`;
    overlay.style.height = inlineCurrency ? '100%' : `${display.offsetHeight}px`;

    const key = JSON.stringify([display.value, decimal, group, precision, suffix, displayedCurrency]);
    if (previousPrimaryKey !== key) {
      previousPrimaryKey = key;
      const number = document.createElement('span');
      number.className = 'money-amount__number';
      number.dataset.moneyInputNumber = '';
      const appendPart = (type, value) => {
        const part = document.createElement('span');
        part.className = `money-amount__${type}`;
        part.dataset[`moneyInput${type[0].toUpperCase()}${type.slice(1)}`] = '';
        part.textContent = value;
        number.append(part);
      };
      match.groups.whole.split(group).forEach((digits, index) => {
        if (index > 0) appendPart('group', group);
        appendPart('integer', digits);
      });
      if (match.groups.fraction !== undefined) {
        appendPart('decimal', decimal);
        if (match.groups.fraction !== '') appendPart('fraction', match.groups.fraction);
      }
      text.replaceChildren(number);
      if (displayedCurrency) {
        const code = document.createElement('span');
        code.className = 'money-amount__currency';
        code.dataset.moneyInputSuffix = '';
        code.textContent = displayedCurrency;
        text.append(code);
      }
    }
    overlay.hidden = false;
    field.classList.add(`${namespace}__field--split`, `${namespace}__field--compact`);
    const active = document.activeElement === display;
    field.classList.toggle(`${namespace}__field--editing`, active);
    if (nativeOverflow) {
      const number = text.querySelector('[data-money-input-number]');
      const code = text.querySelector('[data-money-input-suffix]');
      const numericWidth = number.getBoundingClientRect().width;
      const canWrapCurrency = currencyLabel &&
        getComputedStyle(field).getPropertyValue('--money-input-wrap-currency').trim() === '1';
      let wrapCurrency = false;
      if (canWrapCurrency) {
        // Measure the text, not the label's box: once wrapped its box fills
        // the row, so using that width would prevent a return to one line.
        const currencyRange = document.createRange();
        currencyRange.selectNodeContents(currencyLabel);
        const currencyWidth = currencyRange.getBoundingClientRect().width;
        wrapCurrency = numericWidth + 1 + currencyWidth + 4 > field.clientWidth + 0.5;
      }
      field.classList.toggle(`${namespace}__field--currency-wrap`, wrapCurrency);
      const inputWidth = display.clientWidth;
      const overflowing = numericWidth > inputWidth + 0.5;
      field.classList.toggle(`${namespace}__field--native-overflow`, overflowing);
      if (code) code.hidden = overflowing || wrapCurrency;
      const changed = lastNativeValue !== display.value || lastNativeWidth !== inputWidth;
      lastNativeValue = display.value;
      lastNativeWidth = inputWidth;
      // Moving the currency off the row can make the complete number fit.
      // Discard only the obsolete scroll from that narrower layout.
      if (wrapCurrency && !overflowing && changed) display.scrollLeft = 0;
      if (overflowing || wrapCurrency) {
        // Visa's autosizer changes the width after the browser's input event.
        // Finish native end-caret scrolling after that resize, without moving
        // the selection or overriding manual scrolling on later scroll events.
        if (overflowing && active && changed && display.selectionStart === display.value.length &&
            display.selectionEnd === display.value.length) display.scrollLeft = display.scrollWidth;
        overlay.style.left = `${display.offsetLeft}px`;
        overlay.style.width = `${display.offsetWidth}px`;
        overlay.style.top = `${display.offsetTop}px`;
        overlay.style.height = `${display.offsetHeight}px`;
      }
      // The painted number always follows the real editor, including blur.
      // A synthetic resting offset would move the glyph under the next tap.
      text.style.transform = `translateX(${-display.scrollLeft}px)`;
      field.dataset.moneyScrollStart = String(overflowing && display.scrollLeft > 0.5);
      field.dataset.moneyScrollEnd = String(overflowing && display.scrollLeft + inputWidth < numericWidth - 0.5);
      return;
    }
    // Native scrolling also moves its caret and selection/drag handles. At
    // rest preserve the trailing cents/currency in a constrained viewport.
    const offset = active ? display.scrollLeft :
      Math.max(0, text.getBoundingClientRect().width - viewport.getBoundingClientRect().width);
    text.style.transform = `translateX(${-offset}px)`;
  }

  function render() {
    frame = undefined;
    if (destroyed || !display.isConnected) return;
    if (generic) return renderPrimary();
    const { decimal, group, precision, suffix = false } = readFormat();
    if (!decimal || !group || !Number.isInteger(precision) || precision < 1 || precision > 4) return hide();
    const whole = `(?:\\d+|[1-9]\\d{0,2}(?:${escapePattern(group)}\\d{3})+)`;
    const pattern = new RegExp(`^(${whole})(${escapePattern(decimal)})(\\d{1,${precision}})${suffix ? '( [A-Z]{2,8})?' : ''}$`);
    const match = composing || display.disabled || display.validity.customError ? null : pattern.exec(display.value);
    const styled = getComputedStyle(field).getPropertyValue('--money-input-overlay-ready').trim() === '1';
    if (!match || !styled) return hide();
    const style = getComputedStyle(display);
    ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontStretch', 'fontVariant', 'fontKerning',
      'fontVariantNumeric', 'fontFeatureSettings', 'letterSpacing', 'lineHeight', 'textAlign',
      'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
      'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'borderRadius']
      .forEach(property => { overlay.style[property] = style[property]; });
    overlay.style.top = `${display.offsetTop}px`;
    overlay.style.left = `${display.offsetLeft}px`;
    overlay.style.width = `${display.offsetWidth}px`;
    overlay.style.height = `${display.offsetHeight}px`;

    if (previousValue !== display.value) {
      previousValue = display.value;
      const integer = document.createElement('span');
      integer.dataset[`${dataPrefix}Integer`] = '';
      integer.textContent = match[1];
      const decimals = document.createElement('span');
      decimals.className = `${namespace}__decimals`;
      decimals.dataset[`${dataPrefix}Decimals`] = '';
      decimals.dataset.decimals = match[2] + match[3];
      [...(match[2] + match[3])].forEach(character => {
        const cell = document.createElement('span');
        cell.className = `${namespace}__decimal-character`;
        cell.dataset.character = character;
        cell.textContent = character;
        decimals.append(cell);
      });
      const currency = document.createElement('span');
      currency.dataset[`${dataPrefix}Suffix`] = '';
      currency.textContent = match[4] || '';
      const baseline = document.createElement('span');
      baseline.className = `${namespace}__baseline`;
      text.replaceChildren(integer, decimals, currency, baseline);
    }
    overlay.hidden = false;
    text.style.transform = `translateX(${-display.scrollLeft}px)`;
    const cell = text.querySelector(`.${namespace}__decimal-character`);
    const baseline = text.querySelector(`.${namespace}__baseline`);
    const baselineOffset = baseline.getBoundingClientRect().top - cell.getBoundingClientRect().top;
    text.style.setProperty('--money-input-baseline', `${baselineOffset}px`);
    field.classList.add(`${namespace}__field--split`);
  }

  function refresh() {
    if (!destroyed && frame === undefined) frame = requestAnimationFrame(render);
  }
  function compositionStart() { composing = true; hide(); }
  function compositionEnd() { composing = false; refresh(); }
  function focus() {
    if (namespace !== 'money-input-presentation') return;
    if (frame !== undefined) cancelAnimationFrame(frame);
    render();
  }
  const events = ['input', 'change', 'keyup', 'mouseup', 'select', 'focus', 'blur', 'scroll', 'autoNumeric:formatted', 'money-input:refresh'];
  events.forEach(event => display.addEventListener(event, refresh));
  display.addEventListener('focus', focus);
  display.addEventListener('compositionstart', compositionStart);
  display.addEventListener('compositionend', compositionEnd);
  if (generic) document.addEventListener('selectionchange', selectionChange);
  const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(refresh) : null;
  resize?.observe(display);
  if (inlineCurrency) resize?.observe(field);
  if (namespace === 'money-input-presentation') resize?.observe(text.parentElement);
  window.addEventListener('load', refresh);
  window.addEventListener('resize', refresh);
  document.fonts?.addEventListener('loadingdone', refresh);
  refresh();
  return {
    refresh,
    destroy() {
      destroyed = true;
      if (frame !== undefined) cancelAnimationFrame(frame);
      resize?.disconnect();
      events.forEach(event => display.removeEventListener(event, refresh));
      display.removeEventListener('focus', focus);
      display.removeEventListener('compositionstart', compositionStart);
      display.removeEventListener('compositionend', compositionEnd);
      if (generic) document.removeEventListener('selectionchange', selectionChange);
      window.removeEventListener('load', refresh);
      window.removeEventListener('resize', refresh);
      document.fonts?.removeEventListener('loadingdone', refresh);
      hide();
      if (createdOverlay) overlay.remove();
    },
  };
}

function inputsIn(root) {
  const inputs = root.matches?.('[data-money-input-display]') ? [root] : [];
  inputs.push(...(root.querySelectorAll?.('[data-money-input-display]') || []));
  return inputs;
}

export function initializeMoneyInputPresentations(root = document) {
  inputsIn(root).forEach(display => {
    if (instances.has(display)) return;
    instances.set(display, createMoneyInputPresentation(display, () => {
      const english = display.dataset.moneyInputLocale?.toLowerCase().startsWith('en');
      return { decimal: english ? '.' : ',', group: english ? ',' : '.',
        precision: Number(display.dataset.moneyInputPrecision), suffix: true };
    }));
  });
}

function boot() {
  // Vue can insert these inputs after boot or replace them on remount. Only
  // added/removed subtrees and relevant input attributes are inspected.
  new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'attributes') return instances.get(mutation.target)?.refresh();
      mutation.removedNodes.forEach(node => {
        if (node.isConnected) return;
        inputsIn(node).forEach(display => {
          instances.get(display)?.destroy();
          instances.delete(display);
        });
      });
      mutation.addedNodes.forEach(node => initializeMoneyInputPresentations(node));
    });
  }).observe(document.body, { childList: true, subtree: true, attributes: true,
    attributeFilter: ['data-money-input-locale', 'data-money-input-precision', 'data-money-input-currency', 'disabled', 'readonly', 'aria-invalid', 'rawvalue'] });
  initializeMoneyInputPresentations();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
